import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, FileDown, Pencil, Send, GitBranch, ShoppingCart, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useQuote, useQuoteAction, useQuoteEvents, useQuoteVersions, quoteToDoc, QUOTE_STATUS_LABEL, QUOTE_STATUS_VARIANT } from "./api";
import { DocumentEditor } from "@/modules/documents/DocumentEditor";

import { dateShort, dateTime, money } from "@/lib/format";
import { DeleteButton } from "@/components/shared/DeleteDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EVENT_LABEL: Record<string, string> = {
  created: "Créé", updated: "Modifié", sent: "Envoyé", viewed: "Consulté par le client", negotiating: "En négociation",
  accepted: "Accepté", rejected: "Refusé", expired: "Expiré", version: "Nouvelle version", converted: "Converti en commande",
};

export default function QuotePage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { data: q, isLoading } = useQuote(id);
  const { data: versions } = useQuoteVersions(q?.root_id);
  const { data: events } = useQuoteEvents(id);
  const actions = useQuoteAction(id!);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  if (isLoading) return <p className="text-sm text-muted-foreground">Chargement…</p>;
  if (!q) return <p className="text-sm text-destructive">Devis introuvable.</p>;

  const publicUrl = `${location.origin}/p/devis/${q.public_token}`;
  const isDraft = q.status === "draft";
  const isOpen = ["sent", "viewed", "negotiating"].includes(q.status);
  const latest = !q.superseded_by;

  async function openSend() { setTo(q!.clients?.billing_email ?? ""); setSending(true); }

  return (
    <div>
      <Link to="/devis" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Devis</Link>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-heading text-2xl font-bold">{q.number ?? "Brouillon"}{q.version > 1 && <span className="ml-2 text-base font-medium text-muted-foreground">v{q.version}</span>}</h1>
            <Badge variant={QUOTE_STATUS_VARIANT[q.status]}>{QUOTE_STATUS_LABEL[q.status]}</Badge>
            {!latest && <Badge variant="outline">remplacé par une version plus récente</Badge>}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            <Link to={`/clients/${q.client_id}`} className="font-medium text-foreground hover:underline">{q.clients?.legal_name}</Link>
            {q.title ? ` · ${q.title}` : ""} · {dateShort(q.issue_date)}{q.valid_until ? ` · valide jusqu'au ${dateShort(q.valid_until)}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => void import("./pdf").then((m) => m.downloadQuotePdf(q))}><FileDown className="mr-2 h-4 w-4" />PDF</Button>
          {isDraft && <Button variant="outline" onClick={() => nav(`/devis/${q.id}/modifier`)}><Pencil className="mr-2 h-4 w-4" />Modifier</Button>}
          {isDraft && <Button onClick={() => void openSend()}><Send className="mr-2 h-4 w-4" />Envoyer au client</Button>}
          {isOpen && <Button variant="outline" onClick={() => { void navigator.clipboard.writeText(publicUrl); toast.success("Lien copié"); }}><Copy className="mr-2 h-4 w-4" />Copier le lien</Button>}
          {isOpen && <Button variant="outline" onClick={() => void openSend()}><Send className="mr-2 h-4 w-4" />Renvoyer</Button>}
          {isOpen && latest && <Button variant="outline" onClick={() => actions.setStatus.mutate({ status: "accepted", extra: { accepted_by_name: "Accepté manuellement" } })}>Marquer accepté</Button>}
          {isOpen && latest && <Button variant="outline" onClick={() => setRejecting(true)}>Marquer refusé</Button>}
          {!isDraft && latest && q.status !== "accepted" && <Button variant="outline" onClick={() => actions.newVersion.mutate(undefined, { onSuccess: (nid) => nav(`/devis/${nid}/modifier`) })}><GitBranch className="mr-2 h-4 w-4" />Nouvelle version</Button>}
          {q.status === "accepted" && !q.order_id && <Button onClick={() => actions.toOrder.mutate(undefined, { onSuccess: (oid) => nav(`/commandes/${oid}`) })}><ShoppingCart className="mr-2 h-4 w-4" />Créer la commande</Button>}
          {q.order_id && <Button onClick={() => nav(`/commandes/${q.order_id}`)}><ShoppingCart className="mr-2 h-4 w-4" />Voir la commande</Button>}
          <DeleteButton entity="quote" id={q.id} variant="outline" onDeleted={() => nav("/devis")} />
        </div>
      </div>

      {q.status === "accepted" && (
        <div className="mb-4 rounded-2xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm">
          <span className="font-semibold text-primary">Accepté</span> le {dateTime(q.accepted_at)}{q.accepted_by_name ? ` par ${q.accepted_by_name}` : ""}{q.accepted_ip ? ` (IP ${q.accepted_ip})` : ""}.
          {q.order_id ? " La commande est créée, en attente du contrat signé et de l'acompte." : " Crée la commande pour démarrer."}
        </div>
      )}
      {q.status === "rejected" && q.rejection_reason && <div className="mb-4 rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm"><span className="font-semibold text-destructive">Refusé</span> — {q.rejection_reason}</div>}

      <DocumentEditor doc={quoteToDoc(q)} onChange={() => undefined} clientId={q.client_id} readOnly />

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <section className="glass rounded-2xl p-5">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Conditions</h3>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">Acompte</dt><dd>{Number(q.deposit_pct)} % — {money(q.deposit_amount, q.currency)}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Solde</dt><dd>{money(q.balance_amount, q.currency)} à {q.payment_days} jours</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Régime fiscal</dt><dd>{q.tax_label ?? "—"} ({Number(q.tax_rate_pct)} %)</dd></div>
            {q.tax_mention && <div className="text-xs text-muted-foreground">{q.tax_mention}</div>}
          </dl>
          {q.notes_client && <><h3 className="mb-1 mt-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Notes client</h3><p className="whitespace-pre-wrap text-sm">{q.notes_client}</p></>}
          {q.notes_internal && <><h3 className="mb-1 mt-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Notes internes</h3><p className="whitespace-pre-wrap text-sm text-muted-foreground">{q.notes_internal}</p></>}
          {!isDraft && (
            <div className="mt-4">
              <h3 className="mb-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Lien client</h3>
              <a href={publicUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 break-all text-xs text-primary hover:underline">{publicUrl}<ExternalLink className="h-3 w-3 shrink-0" /></a>
            </div>
          )}
        </section>
        <section className="glass rounded-2xl p-5">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Versions</h3>
          <ul className="space-y-1 text-sm">
            {versions?.map((v) => (
              <li key={v.id} className="flex items-center justify-between gap-2">
                <Link to={`/devis/${v.id}`} className={v.id === q.id ? "font-semibold" : "hover:underline"}>v{v.version} {v.number ? `· ${v.number}` : "· brouillon"}</Link>
                <span className="text-muted-foreground">{money(v.total, q.currency)} · {QUOTE_STATUS_LABEL[v.status]}</span>
              </li>
            ))}
          </ul>
        </section>
        <section className="glass rounded-2xl p-5">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Historique</h3>
          <ul className="space-y-1.5 text-sm">
            {events?.map((e) => {
              const who = e.profiles as { full_name: string | null; email: string } | null;
              return <li key={e.id} className="flex justify-between gap-2"><span>{EVENT_LABEL[e.type] ?? e.type}{e.actor_name ? ` — ${e.actor_name}` : who ? ` — ${who.full_name ?? who.email}` : ""}</span><span className="shrink-0 text-muted-foreground">{dateTime(e.created_at)}</span></li>;
            })}
            {events && events.length === 0 && <li className="text-muted-foreground">Aucun événement.</li>}
          </ul>
        </section>
      </div>

      <Dialog open={sending} onOpenChange={setSending}>
        <DialogContent>
          <DialogHeader><DialogTitle>Envoyer le devis</DialogTitle><DialogDescription>Le client reçoit un email avec un lien pour consulter, télécharger et accepter le devis en ligne. {isDraft && "Le devis reçoit son numéro et devient non modifiable."}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label htmlFor="send-to">Destinataire *</Label><Input id="send-to" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="compta@client.fr" /></div>
            <div className="space-y-1.5"><Label htmlFor="send-msg">Message (facultatif)</Label><Textarea id="send-msg" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Bonjour, veuillez trouver notre proposition…" /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setSending(false)}>Annuler</Button>
            <Button disabled={!/^\S+@\S+\.\S+$/.test(to) || actions.send.isPending} onClick={() => actions.send.mutate({ to: to.trim(), message: message.trim() || undefined }, { onSuccess: () => setSending(false) })}><Send className="mr-2 h-4 w-4" />Envoyer</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={rejecting} onOpenChange={setRejecting}>
        <DialogContent>
          <DialogHeader><DialogTitle>Marquer le devis refusé</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label htmlFor="reject-reason">Motif</Label><Textarea id="reject-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} /></div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setRejecting(false)}>Annuler</Button>
            <Button variant="destructive" onClick={() => actions.setStatus.mutate({ status: "rejected", extra: { rejection_reason: reason.trim() || null } }, { onSuccess: () => setRejecting(false) })}>Refusé</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
